import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Empty } from '@/components/ui/Empty';
import { useInstructors, type Instructor } from '@/data/instructors';
import {
  useStaffPayroll,
  useInstructorAvailability,
  useTimeClock,
  useClockIn,
  useClockOut,
  useClassSubstitutions,
  type StaffPayrollRow,
} from '@/data/staff';
import { formatTime12 } from '@/lib/time';
import { toast } from '@/lib/toast';

// Staff Operations console. Four sections, built on the same layout primitives
// as the owner BI dashboard / owner dashboard (StatCard / Section / SimpleTable
// / Num) so it reads as one product:
//   1. Payroll export — date range -> staff_payroll_export(), with hours totals
//      and a client-side CSV download.
//   2. Faculty + weekly availability — instructor list; expand to see blocks.
//   3. Time clock — recent entries for the selected instructor + clock in/out.
//   4. Substitutions — request list (read-only here).
//
// The /admin parent route gates on the `admin` role server-side, so this child
// route inherits that guard (matches every other admin.* route).
export const Route = createFileRoute('/admin/staff/')({
  component: StaffOps,
});

type BadgeTone = NonNullable<BadgeProps['tone']>;

// ---------------------------------------------------------------------------
// Layout primitives (mirror admin.bi.index.tsx / admin.dashboard.tsx)
// ---------------------------------------------------------------------------

function StatCard({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <article className="surface-elevated p-5">
      <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">{label}</p>
      <p className="mt-2 font-[var(--font-display)] text-[var(--text-3xl)] tabular-nums">{value}</p>
      {hint && <p className="mt-1 text-[var(--color-ink-subtle)] text-[var(--text-xs)]">{hint}</p>}
    </article>
  );
}

function Section({
  id,
  title,
  description,
  action,
  children,
}: {
  id: string;
  title: string;
  description: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={`${id}-heading`} className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id={`${id}-heading`} className="text-[var(--text-xl)]">
            {title}
          </h2>
          <p className="mt-1 max-w-3xl text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            {description}
          </p>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function SimpleTable({ columns, rows }: { columns: string[]; rows: ReactNode[][] }) {
  return (
    <div className="surface overflow-x-auto">
      <table className="min-w-full text-left text-[var(--text-sm)]">
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            {columns.map((c) => (
              <th key={c} className="px-4 py-3 font-medium">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-[var(--color-border)] last:border-0">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-3 align-top">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Num({ value }: { value: number }) {
  return <span className="font-medium tabular-nums">{value}</span>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function instructorName(i: Pick<Instructor, 'first_name' | 'last_name'>): string {
  return `${i.first_name} ${i.last_name}`.trim();
}

/** Inclusive `YYYY-MM-DD` for `daysAgo` before today (local). */
function isoDaysAgo(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Wrap a CSV cell, quoting when it contains a comma, quote, or newline. */
function csvCell(value: string | number): string {
  const s = String(value);
  return /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
}

function payrollToCsv(rows: StaffPayrollRow[]): string {
  const header = ['Instructor ID', 'First name', 'Last name', 'Entries', 'Total hours'];
  const lines = rows.map((r) =>
    [r.instructor_id, r.first_name, r.last_name, r.entries, r.total_hours].map(csvCell).join(','),
  );
  return [header.join(','), ...lines].join('\n');
}

function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Payroll
// ---------------------------------------------------------------------------

function PayrollSection() {
  const [start, setStart] = useState(() => isoDaysAgo(14));
  const [end, setEnd] = useState(() => today());
  const payroll = useStaffPayroll(start, end);

  const rows = useMemo(() => payroll.data ?? [], [payroll.data]);
  const totalHours = useMemo(
    () => rows.reduce((sum, r) => sum + (Number.isFinite(r.total_hours) ? r.total_hours : 0), 0),
    [rows],
  );
  const totalEntries = useMemo(
    () => rows.reduce((sum, r) => sum + (Number.isFinite(r.entries) ? r.entries : 0), 0),
    [rows],
  );

  function handleExport() {
    if (rows.length === 0) return;
    downloadCsv(`pirouette-payroll-${start}_${end}.csv`, payrollToCsv(rows));
    toast({
      title: 'Export ready',
      description: `${rows.length} instructor ${rows.length === 1 ? 'row' : 'rows'} exported`,
      variant: 'success',
    });
  }

  return (
    <Section
      id="payroll"
      title="Payroll export"
      description="Hours and clock-entry counts per instructor for a date range, from staff_payroll_export. Export a CSV for your payroll provider."
      action={
        <Button onClick={handleExport} disabled={rows.length === 0 || payroll.isLoading} size="sm">
          Export CSV
        </Button>
      }
    >
      <div className="surface-elevated flex flex-wrap items-end gap-4 p-5">
        <div>
          <Label htmlFor="payroll-start">From</Label>
          <Input
            id="payroll-start"
            type="date"
            value={start}
            max={end}
            onChange={(e) => setStart(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="payroll-end">To</Label>
          <Input
            id="payroll-end"
            type="date"
            value={end}
            min={start}
            onChange={(e) => setEnd(e.target.value)}
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard label="Instructors" value={rows.length} hint="With entries in range" />
        <StatCard
          label="Total hours"
          value={totalHours.toFixed(2)}
          hint="Summed across instructors"
        />
        <StatCard label="Clock entries" value={totalEntries} hint="Completed in range" />
      </div>

      {payroll.isLoading ? (
        <p aria-live="polite">Loading…</p>
      ) : payroll.error ? (
        <Empty
          title="Payroll unavailable"
          description="The payroll export could not be loaded for this range. Adjust the dates or refresh to retry."
        />
      ) : rows.length === 0 ? (
        <Empty
          title="No payroll in range"
          description="No instructor has completed clock entries in this window. Adjust the dates to widen the search."
        />
      ) : (
        <SimpleTable
          columns={['Instructor', 'Entries', 'Total hours']}
          rows={rows.map((r) => [
            <span className="font-medium">{instructorName(r)}</span>,
            <Num value={r.entries} />,
            <span className="tabular-nums">{r.total_hours.toFixed(2)}</span>,
          ])}
        />
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Faculty + availability
// ---------------------------------------------------------------------------

const WEEKDAY_ORDER: Record<string, number> = {
  monday: 0,
  tuesday: 1,
  wednesday: 2,
  thursday: 3,
  friday: 4,
  saturday: 5,
  sunday: 6,
};

function availabilityTone(available: boolean): BadgeTone {
  return available ? 'success' : 'neutral';
}

function AvailabilityList({ instructorId }: { instructorId: string }) {
  const availability = useInstructorAvailability(instructorId);
  const blocks = useMemo(() => {
    const data = availability.data ?? [];
    return [...data].sort(
      (a, b) =>
        (WEEKDAY_ORDER[a.weekday.toLowerCase()] ?? 99) -
          (WEEKDAY_ORDER[b.weekday.toLowerCase()] ?? 99) ||
        a.start_time.localeCompare(b.start_time),
    );
  }, [availability.data]);

  if (availability.isLoading) {
    return <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">Loading…</p>;
  }
  if (availability.error) {
    return (
      <p className="text-[var(--color-danger)] text-[var(--text-sm)]">
        Availability could not be loaded.
      </p>
    );
  }
  if (blocks.length === 0) {
    return (
      <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
        No availability recorded.
      </p>
    );
  }
  return (
    <ul className="flex flex-wrap gap-2">
      {blocks.map((b) => (
        <li key={b.id}>
          <Badge tone={availabilityTone(b.available)}>
            <span className="capitalize">{b.weekday}</span> {formatTime12(b.start_time)}–
            {formatTime12(b.end_time)}
            {!b.available && ' · off'}
          </Badge>
        </li>
      ))}
    </ul>
  );
}

function FacultySection({
  instructors,
  selectedId,
  onSelect,
}: {
  instructors: Instructor[];
  selectedId: string | undefined;
  onSelect: (id: string) => void;
}) {
  return (
    <Section
      id="faculty"
      title="Faculty & availability"
      description="Each instructor's weekly availability blocks. Select an instructor to manage their time clock below."
    >
      <ul className="grid gap-4 lg:grid-cols-2">
        {instructors.map((i) => {
          const isSelected = i.id === selectedId;
          return (
            <li
              key={i.id}
              className={`surface space-y-3 p-5 ${
                isSelected ? 'ring-2 ring-[var(--color-accent)]' : ''
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-medium">{instructorName(i)}</p>
                  {i.email && (
                    <p className="text-[var(--color-ink-muted)] text-[var(--text-xs)]">{i.email}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {!i.active && <Badge tone="neutral">inactive</Badge>}
                  <Button
                    variant={isSelected ? 'primary' : 'secondary'}
                    size="sm"
                    onClick={() => onSelect(i.id)}
                    aria-pressed={isSelected}
                  >
                    {isSelected ? 'Selected' : 'Time clock'}
                  </Button>
                </div>
              </div>
              <AvailabilityList instructorId={i.id} />
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Time clock
// ---------------------------------------------------------------------------

/** Hours between clock_in and clock_out, or null when still open. */
function entryHours(clockIn: string, clockOut: string | null): number | null {
  if (!clockOut) return null;
  const ms = new Date(clockOut).getTime() - new Date(clockIn).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  return ms / 3_600_000;
}

function formatStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function TimeClockSection({ instructor }: { instructor: Instructor | undefined }) {
  const timeClock = useTimeClock(instructor?.id);
  const clockIn = useClockIn();
  const clockOut = useClockOut();

  const entries = timeClock.data ?? [];
  const openEntry = entries.find((e) => e.clock_out === null);
  const pending = clockIn.isPending || clockOut.isPending;

  function handleClockIn() {
    if (!instructor) return;
    clockIn.mutate(
      { instructorId: instructor.id },
      {
        onSuccess: () =>
          toast({
            title: 'Clocked in',
            description: instructorName(instructor),
            variant: 'success',
          }),
        onError: (err) =>
          toast({
            title: 'Clock-in failed',
            description: err instanceof Error ? err.message : 'Unknown error',
            variant: 'error',
          }),
      },
    );
  }

  function handleClockOut() {
    if (!instructor || !openEntry) return;
    clockOut.mutate(
      { entryId: openEntry.id, instructorId: instructor.id },
      {
        onSuccess: () =>
          toast({
            title: 'Clocked out',
            description: instructorName(instructor),
            variant: 'success',
          }),
        onError: (err) =>
          toast({
            title: 'Clock-out failed',
            description: err instanceof Error ? err.message : 'Unknown error',
            variant: 'error',
          }),
      },
    );
  }

  return (
    <Section
      id="time-clock"
      title="Time clock"
      description={
        instructor
          ? `Recent clock entries for ${instructorName(instructor)}. Clock in to start a shift; clock out closes the open entry.`
          : 'Select an instructor above to view their recent clock entries and clock in or out.'
      }
      action={
        instructor ? (
          openEntry ? (
            <Button variant="danger" size="sm" onClick={handleClockOut} disabled={pending}>
              Clock out
            </Button>
          ) : (
            <Button size="sm" onClick={handleClockIn} disabled={pending}>
              Clock in
            </Button>
          )
        ) : undefined
      }
    >
      {!instructor ? (
        <Empty
          title="No instructor selected"
          description="Pick an instructor from the faculty list to manage their time clock."
        />
      ) : timeClock.isLoading ? (
        <p aria-live="polite">Loading…</p>
      ) : timeClock.error ? (
        <Empty
          title="Time clock unavailable"
          description="Recent clock entries could not be loaded for this instructor."
        />
      ) : entries.length === 0 ? (
        <Empty
          title="No clock entries yet"
          description="This instructor has no recorded shifts. Clock in to create the first entry."
        />
      ) : (
        <SimpleTable
          columns={['Clock in', 'Clock out', 'Hours', 'Status']}
          rows={entries.map((e) => {
            const hours = entryHours(e.clock_in, e.clock_out);
            return [
              <span className="tabular-nums">{formatStamp(e.clock_in)}</span>,
              <span className="text-[var(--color-ink-muted)] tabular-nums">
                {e.clock_out ? formatStamp(e.clock_out) : '—'}
              </span>,
              <span className="tabular-nums">{hours === null ? '—' : hours.toFixed(2)}</span>,
              e.clock_out ? (
                <Badge tone="neutral">closed</Badge>
              ) : (
                <Badge tone="success">open</Badge>
              ),
            ];
          })}
        />
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Substitutions (read-only for v1)
// ---------------------------------------------------------------------------

function substitutionTone(status: string): BadgeTone {
  if (status === 'confirmed') return 'success';
  if (status === 'declined') return 'danger';
  return 'warn'; // requested / anything else pending
}

function SubstitutionsSection({ instructors }: { instructors: Instructor[] }) {
  const subs = useClassSubstitutions();
  const nameById = useMemo(
    () => new Map(instructors.map((i) => [i.id, instructorName(i)])),
    [instructors],
  );
  const rows = subs.data ?? [];

  return (
    <Section
      id="substitutions"
      title="Substitutions"
      description="Substitute-instructor requests across class sessions. Requested items await confirmation."
    >
      {/* TODO: substitution-approval workflow (confirm / decline + notify the
          original instructor) — out of scope for v1, which is read-only. */}
      {subs.isLoading ? (
        <p aria-live="polite">Loading…</p>
      ) : subs.error ? (
        <Empty
          title="Substitutions unavailable"
          description="The substitution list could not be loaded for this session."
        />
      ) : rows.length === 0 ? (
        <Empty
          title="No substitution requests"
          description="No substitutions have been requested. New requests will appear here as they are raised."
        />
      ) : (
        <SimpleTable
          columns={['Substitute', 'Session', 'Reason', 'Status']}
          rows={rows.map((s) => [
            <span className="font-medium">
              {nameById.get(s.substitute_instructor_id) ?? s.substitute_instructor_id}
            </span>,
            <span className="font-mono text-[var(--color-ink-muted)] text-[var(--text-xs)]">
              {s.class_session_id.slice(0, 8)}
            </span>,
            <span className="text-[var(--color-ink-muted)]">{s.reason ?? '—'}</span>,
            <Badge tone={substitutionTone(s.status)}>{s.status}</Badge>,
          ])}
        />
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

function StaffOps() {
  const instructorsQuery = useInstructors();
  const instructors = useMemo(() => instructorsQuery.data ?? [], [instructorsQuery.data]);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  // Default the time-clock selection to the first instructor once loaded.
  const effectiveSelectedId = selectedId ?? instructors[0]?.id;
  const selectedInstructor = instructors.find((i) => i.id === effectiveSelectedId);

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Operations
        </p>
        <h1 className="mt-2">Staff Operations</h1>
      </header>

      <PayrollSection />

      {instructorsQuery.isLoading ? (
        <p aria-live="polite">Loading faculty…</p>
      ) : instructorsQuery.error ? (
        <Empty
          title="Faculty unavailable"
          description="The instructor roster could not be loaded for this session. Refresh to retry."
        />
      ) : instructors.length === 0 ? (
        <Empty
          title="No instructors yet"
          description="Add instructors from the Classes area to start tracking availability and time."
        />
      ) : (
        <>
          <FacultySection
            instructors={instructors}
            selectedId={effectiveSelectedId}
            onSelect={setSelectedId}
          />
          <TimeClockSection instructor={selectedInstructor} />
          <SubstitutionsSection instructors={instructors} />
        </>
      )}
    </div>
  );
}
